/**
 * Where the figures of a drawn outline go — dimensioned the way a technical
 * drawing is (Denis, 20.10.2026: on L4 the figures of the short steps ran
 * over each other and over the walls).
 *
 * Every figure gets a dimension line parallel to its wall, on the wall's
 * outside, with an extension line from each end of the wall to it. The
 * figure sits on the line's outer side, centred. Three things keep the
 * drawing readable:
 *
 * - **The outside comes from the outline, not from its middle.** A step
 *   pulled into the shape faces its neighbour; "away from the centre" put
 *   the figure into that neighbour. Here the side is the one the walk does
 *   not enclose (the walk closed back to its start, for an open one).
 * - **A figure longer than its wall goes past the end of the line**, the
 *   line carried on under it — the CAD way for short dimensions.
 * - **No figure lands on another or on a wall.** Each is tried on its line,
 *   past either end, and on two further tiers outwards; the first free place
 *   wins. Short walls are placed first: a long one has room to give way.
 *
 * Screen coordinates throughout; nothing here knows millimetres.
 */

import type { ScreenPoint } from "./projection";

export interface DimensionInput {
  /** The wall's index in the walk — what the plan is keyed by. */
  index: number;
  from: ScreenPoint;
  to: ScreenPoint;
  /** The figure's width along its wall, in screen units. */
  textWidth: number;
}

export interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface DimensionPlan {
  index: number;
  /** The dimension line, parallel to the wall. */
  line: Segment;
  /** From each end of the wall to a little past the line. */
  extensions: [Segment, Segment];
  /** The figure's centre; `-90` turns it along an upright wall. */
  label: { x: number; y: number; rotation: 0 | -90; width: number };
  /** The line carried on under a figure set past its end — `null` on the line. */
  leader: Segment | null;
  /** 0 on the first line out, 1 and 2 further out. */
  tier: number;
}

/** From the wall to the first dimension line. */
const FIRST_LINE = 14;
/** Between two tiers. */
const TIER_GAP = 16;
const TIERS = 3;
/** How far an extension line runs past its dimension line. */
const OVERSHOOT = 3;
/** From the line to the middle of the figure on its outer side. */
const LABEL_LIFT = 7;
/** The figure's height across its wall. */
const LABEL_HEIGHT = 12;
/** Room a figure needs between the extension lines, beyond its own width. */
const FIT_PAD = 8;
/** Between the line's end and a figure set past it. */
const PAST_GAP = 4;
/** Kept free around every figure. */
const MARGIN = 2;

interface Box {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** The figure's box on the board, upright or turned. */
export function labelBox(plan: DimensionPlan): Box {
  const along = plan.label.width / 2;
  const across = LABEL_HEIGHT / 2;
  const [w, h] = plan.label.rotation === 0 ? [along, across] : [across, along];
  return { x1: plan.label.x - w, y1: plan.label.y - h, x2: plan.label.x + w, y2: plan.label.y + h };
}

function insideWalk(walk: ScreenPoint[], p: ScreenPoint): boolean {
  let hit = false;
  for (let i = 0, j = walk.length - 1; i < walk.length; j = i++) {
    const a = walk[i]!;
    const b = walk[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) hit = !hit;
  }
  return hit;
}

function boxesMeet(a: Box, b: Box): boolean {
  return a.x1 < b.x2 + MARGIN && b.x1 < a.x2 + MARGIN && a.y1 < b.y2 + MARGIN && b.y1 < a.y2 + MARGIN;
}

function boxMeetsSegment(box: Box, a: ScreenPoint, b: ScreenPoint): boolean {
  // Walls are axis-parallel, so a wall is a box of no width.
  const wall = { x1: Math.min(a.x, b.x), y1: Math.min(a.y, b.y), x2: Math.max(a.x, b.x), y2: Math.max(a.y, b.y) };
  return box.x1 < wall.x2 + MARGIN && wall.x1 < box.x2 + MARGIN && box.y1 < wall.y2 + MARGIN && wall.y1 < box.y2 + MARGIN;
}

/**
 * Plan the dimension of every wall in `inputs` over the walk `walk` (screen
 * points, a closed one carrying its start twice). Walls not in `inputs` —
 * those without a figure — get none, and are only something to keep clear of.
 */
export function placeDimensions(walk: ScreenPoint[], closed: boolean, inputs: DimensionInput[]): DimensionPlan[] {
  const ring = closed ? walk.slice(0, -1) : walk;
  const placed: Box[] = [];
  const plans: DimensionPlan[] = [];

  const order = [...inputs].sort(
    (a, b) => Math.hypot(a.to.x - a.from.x, a.to.y - a.from.y) - Math.hypot(b.to.x - b.from.x, b.to.y - b.from.y),
  );

  for (const input of order) {
    const { from, to, textWidth } = input;
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy) || 1;
    const ux = dx / length;
    const uy = dy / length;
    // Unit normal, turned to the side the walk does not enclose.
    let nx = -uy;
    let ny = ux;
    const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
    if (ring.length >= 3 && insideWalk(ring, { x: mid.x + nx * 3, y: mid.y + ny * 3 })) {
      nx = -nx;
      ny = -ny;
    }
    const upright = Math.abs(dy) > Math.abs(dx);
    const fits = textWidth + FIT_PAD <= length;

    const candidate = (tier: number, where: "mid" | "past-end" | "past-start"): DimensionPlan => {
      const offset = FIRST_LINE + tier * TIER_GAP;
      const line = {
        x1: from.x + nx * offset,
        y1: from.y + ny * offset,
        x2: to.x + nx * offset,
        y2: to.y + ny * offset,
      };
      const extensions: [Segment, Segment] = [
        { x1: from.x, y1: from.y, x2: line.x1 + nx * OVERSHOOT, y2: line.y1 + ny * OVERSHOOT },
        { x1: to.x, y1: to.y, x2: line.x2 + nx * OVERSHOOT, y2: line.y2 + ny * OVERSHOOT },
      ];
      let centre = { x: (line.x1 + line.x2) / 2, y: (line.y1 + line.y2) / 2 };
      let leader: Segment | null = null;
      if (where !== "mid") {
        const sign = where === "past-end" ? 1 : -1;
        const [ex, ey] = where === "past-end" ? [line.x2, line.y2] : [line.x1, line.y1];
        const reach = PAST_GAP + textWidth;
        leader = { x1: ex, y1: ey, x2: ex + sign * ux * reach, y2: ey + sign * uy * reach };
        centre = { x: ex + sign * ux * (PAST_GAP + textWidth / 2), y: ey + sign * uy * (PAST_GAP + textWidth / 2) };
      }
      return {
        index: input.index,
        line,
        extensions,
        label: {
          x: centre.x + nx * LABEL_LIFT,
          y: centre.y + ny * LABEL_LIFT,
          rotation: upright ? -90 : 0,
          width: textWidth,
        },
        leader,
        tier,
      };
    };

    const tries: DimensionPlan[] = [];
    for (let tier = 0; tier < TIERS; tier += 1) {
      if (fits) tries.push(candidate(tier, "mid"));
      tries.push(candidate(tier, "past-end"), candidate(tier, "past-start"));
    }
    const free = tries.find((plan) => {
      const box = labelBox(plan);
      if (placed.some((other) => boxesMeet(box, other))) return false;
      for (let i = 0; i + 1 < walk.length; i += 1) {
        if (boxMeetsSegment(box, walk[i]!, walk[i + 1]!)) return false;
      }
      return true;
    });
    const chosen = free ?? tries[0]!;
    placed.push(labelBox(chosen));
    plans.push(chosen);
  }

  return plans.sort((a, b) => a.index - b.index);
}
